import { styled } from "goober";
import { useMemo } from "react";
import { queries } from "../../../shared/queries";
import { useQuery } from "@rocicorp/zero/react";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { useMakeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { BuildStatus } from "../BuildStatus";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { ListTable } from "../ListTable";
import { Modal } from "../Modal";
import { PressButton } from "../PressButton";
import { SearchField } from "../Settings/shared";
import { Text } from "../Text";
import { WorkflowBadge } from "../WorkflowBadge";
import { computeAiBranchRows } from "./autofixAutotune";
import {
  DETAIL_MODAL_STYLE,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalHeaderRight,
  ModalTitle,
  SummaryCard,
} from "./MonitoringDetailCard";
import { useMonitoringInteraction } from "./analytics";
import { ListSection, NameBody, NameCell } from "./monitoringList";
import { useMonitoringScope } from "./useMonitoringScope";
import { PanelSkeleton } from "./workflowsLayout";

const Tabs = styled("div")`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
`;

const TabButton = styled(PressButton)`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 24px;
  color: var(--text-secondary);
  background: var(--surface-primary);

  &.active,
  &:hover {
    color: var(--text-primary);
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const SplitRow = styled("div")`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  height: 24px;
  gap: 16px;
`;

const SplitStat = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary);
`;

type View = "autofix" | "autotune";

export function AutofixAutotunePanel() {
  const makeUrl = useMakeUrl();
  const recordInteraction = useMonitoringInteraction();
  const formatRelativeTime = useFormatRelativeTime();
  const [data, result] = useQuery(queries.monitorAutofixAutotune());
  const { matchesSpace } = useMonitoringScope(routes.monitoring.workflows);
  const displayName = useSpaceDisplayName();
  const { panel, search: searchParam } = useCurrentUrl(
    routes.monitoring.workflows
  );
  const open = panel === "autofix" || panel === "autotune";
  const view: View = panel === "autotune" ? "autotune" : "autofix";
  const search = searchParam ?? "";
  const setView = (next: View) => {
    recordInteraction("workflows.autofix_autotune_view_toggle", {
      value: next,
    });
    navigate(
      routes.monitoring.workflows,
      { panel: next, search: "" },
      { replace: true }
    );
  };
  const setSearch = (value: string) =>
    navigate(routes.monitoring.workflows, { search: value }, { replace: true });
  const close = () =>
    navigate(
      routes.monitoring.workflows,
      { panel: "", search: "" },
      { replace: true }
    );

  const { autofix, autotune } = useMemo(() => {
    const rows = computeAiBranchRows(data ?? []);
    return {
      autofix: rows.autofix.filter((row) => matchesSpace(row.spaceId)),
      autotune: rows.autotune.filter((row) => matchesSpace(row.spaceId)),
    };
  }, [data, matchesSpace]);

  const workflowById = useMemo(
    () => new Map((data ?? []).map((w) => [w.id, w] as const)),
    [data]
  );

  const total = autofix.length + autotune.length;

  if (result.type !== "complete") {
    return <PanelSkeleton />;
  }

  const activeRows = view === "autofix" ? autofix : autotune;
  const q = search.trim().toLowerCase();
  const filtered = q
    ? activeRows.filter((r) => r.workflowName.toLowerCase().includes(q))
    : activeRows;

  return (
    <>
      <SummaryCard
        title="Autofix and Autotune"
        stacked
        onOpen={
          total > 0
            ? () => {
                const next = autofix.length > 0 ? "autofix" : "autotune";
                recordInteraction("workflows.panel_drilldown_open", {
                  value: next,
                });
                navigate(routes.monitoring.workflows, {
                  panel: next,
                  search: "",
                });
              }
            : undefined
        }
        accessory={
          <SplitRow>
            <SplitStat>
              <Icon name="radar" size={16} />
              <Text variant="text-regular-md" color="var(--text-primary)">
                {autofix.length}
              </Text>
            </SplitStat>
            <SplitStat>
              <Icon name="audio-waveform" size={16} />
              <Text variant="text-regular-md" color="var(--text-primary)">
                {autotune.length}
              </Text>
            </SplitStat>
          </SplitRow>
        }
      />

      <Modal open={open} onClose={close} style={DETAIL_MODAL_STYLE}>
        <ModalHeader>
          <ModalTitle
            icon={view === "autofix" ? "radar" : "audio-waveform"}
            title="Autofix and Autotune"
          />
          <Tabs>
            <TabButton
              className={view === "autofix" ? "active" : undefined}
              aria-pressed={view === "autofix"}
              onPress={() => setView("autofix")}
            >
              <Icon name="radar" size={12} />
              <Text variant="text-small-md">Autofix</Text>
              {autofix.length > 0 ? (
                <Text variant="text-small" color="var(--text-secondary)">
                  {autofix.length}
                </Text>
              ) : null}
            </TabButton>
            <TabButton
              className={view === "autotune" ? "active" : undefined}
              aria-pressed={view === "autotune"}
              onPress={() => setView("autotune")}
            >
              <Icon name="audio-waveform" size={12} />
              <Text variant="text-small-md">Autotune</Text>
              {autotune.length > 0 ? (
                <Text variant="text-small" color="var(--text-secondary)">
                  {autotune.length}
                </Text>
              ) : null}
            </TabButton>
          </Tabs>
          <ModalHeaderRight>
            <Button size="l" variant="secondary" icon="x" onClick={close} />
          </ModalHeaderRight>
        </ModalHeader>

        <ModalBody style={{ gap: 0 }}>
          <ListSection
            title={view === "autofix" ? "Autofix" : "Autotune"}
            description={
              view === "autofix"
                ? "Fixes 3B proposed for failing workflows, awaiting review."
                : "Improvements 3B proposed for your workflows, awaiting review."
            }
            action={
              <SearchField
                placeholder="Search workflows…"
                value={search}
                onChange={setSearch}
                width={280}
                autoFocus
              />
            }
          >
            <ListTable
              headers={{
                name: "Workflow",
                status: "Status",
                updated: "Updated",
              }}
              columns={{ name: "flex", status: "md", updated: "md" }}
              emptyState={
                <Text variant="text-regular" color="var(--text-secondary)">
                  {search
                    ? "No workflows match your search."
                    : `No ${view} branches awaiting review.`}
                </Text>
              }
              items={filtered.map((row) => {
                const space = workflowById.get(row.workflowId)?.space;
                return {
                  key: row.branchId,
                  link: makeUrl(routes.space.workflow, {
                    spaceId: row.spaceId,
                    workflowId: row.workflowId,
                    branchId: row.branchId,
                  }),
                  onNavigate: () =>
                    recordInteraction("workflows.workflow_link_click", {
                      targetType: "workflow",
                      targetId: row.workflowId,
                    }),
                  name: (hover: boolean) => (
                    <NameCell>
                      <WorkflowBadge
                        workflow={{
                          id: row.workflowId,
                          badgeSeed: row.badgeSeed,
                          pausedAt: row.pausedAt,
                          branches: [],
                        }}
                        hover={hover}
                        blueprint
                      />
                      <NameBody>
                        <Text variant="text-regular-md" ellipsis>
                          {row.workflowName}
                        </Text>
                        {space ? (
                          <Text
                            variant="text-small"
                            color="var(--text-secondary)"
                            ellipsis
                          >
                            {displayName(space)}
                          </Text>
                        ) : null}
                      </NameBody>
                    </NameCell>
                  ),
                  status: <BuildStatus status={row.status} />,
                  updated: (
                    <Text variant="text-small" color="var(--text-secondary)">
                      {formatRelativeTime(row.lastActivity)}
                    </Text>
                  ),
                };
              })}
            />
          </ListSection>
        </ModalBody>

        <ModalFooter>
          <Text variant="text-small" color="var(--text-secondary)">
            {total} {total === 1 ? "branch" : "branches"} awaiting review
          </Text>
          <Button size="l" variant="primary" onClick={close}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
